import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import {
  ActivityItem,
  EventLog,
  ReportLogOfEvents,
} from '@report/models/report-of-event-log';
import { ReportOrderItemSectionType } from '@shared/enums';
import { ProjectSetup } from '@shared/models';
import { DprActivitiesDurationHistory, DprActivity } from '@shared/models/dpr';
import {
  Activity,
  ActivityGroup,
  ActivityStream,
  ReportOrderItem,
} from '@shared/models/project-setup';
import {
  computeDiffTimeAsMinutes,
  convertTimeToDate,
  minutesToHoursAndMinutes,
} from '@shared/utils';

@Injectable()
export class ReportOfEventLogService {
  activityGroups: ActivityGroup[];
  activityItems: Activity[];
  activityStreams: ActivityStream[];
  dprActivitiesList: DprActivity[];
  dprActivitiesDurationHistory: DprActivitiesDurationHistory[];
  enableEventsLogActivityGroupsOnly: boolean;

  public constructReportOfEventLog(
    activitiesList: DprActivity[],
    dprActivitiesDurationHistory: DprActivitiesDurationHistory[],
    selectedSubProjectData: ProjectSetup
  ): Observable<ReportLogOfEvents[]> {
    const reportOfEventLogs: ReportLogOfEvents[] = [];

    const reportOrderItems =
      selectedSubProjectData.reportConfiguration.reportOrder.order;
    this.activityGroups = selectedSubProjectData.activityGroupList;
    this.activityItems = selectedSubProjectData.activityList;
    this.activityStreams = selectedSubProjectData.activityStreams;
    this.dprActivitiesList = activitiesList;
    this.dprActivitiesDurationHistory = dprActivitiesDurationHistory;
    this.enableEventsLogActivityGroupsOnly =
      selectedSubProjectData.reportConfiguration.enableEventsLogActivityGroupsOnly;

    reportOrderItems.forEach((orderItem: ReportOrderItem) => {
      if (
        orderItem.reportOrderItemSectionType ===
          ReportOrderItemSectionType.ActivityStream &&
        orderItem.enableEventsLog &&
        this.isActivityStreamActive(orderItem.identifier)
      ) {
        reportOfEventLogs.push({
          activityStream: this.getActivityStreamName(orderItem.identifier),
          reportOfEventLog: this.getEventLog(orderItem.identifier),
        });
      }
    });

    return of(reportOfEventLogs.filter((x) => x.reportOfEventLog.length > 0));
  }

  private getEventLog(activitystreamIdentifier: number): EventLog[] {
    const eventLog: EventLog[] = [];

    this.activityGroups
      .filter(
        (x) =>
          x.isActive &&
          !!this.activityItems.filter(
            (y) => y.isActive && y.activityGroupId === x.id
          ).length
      )
      .forEach((group: ActivityGroup) => {
        if (
          !!this.dprActivitiesDurationHistory.find(
            (z) =>
              z.activityGroupId === group.id &&
              z.parallelActivityType ===
                this.getActivityType(activitystreamIdentifier)
          ) ||
          this.isGroupIncludedInActivityStream(
            group.id,
            this.getActivityType(activitystreamIdentifier)
          )
        ) {
          const activities = this.getActivities(
            group.id,
            activitystreamIdentifier
          );

          if (!activities?.length) {
            return;
          }
          eventLog.push({
            activityGroup: group.name,
            accumulatedDurationToDate:
              this.getActivityGroupAccumulatedDurationToDate(
                group.id,
                this.getActivityType(activitystreamIdentifier),
                this.getTotalMinutes(
                  this.getCurrentActivityStreamGroups(
                    group.id,
                    activitystreamIdentifier
                  )
                )
              ),
            // Add the activities property conditionally to the main object
            ...(!this.enableEventsLogActivityGroupsOnly && {
              activities,
            }),
            totalDurationToday: minutesToHoursAndMinutes(
              this.getTotalMinutes(
                this.getCurrentActivityStreamGroups(
                  group.id,
                  activitystreamIdentifier
                )
              )
            ),
          });
        }
      });

    return eventLog;
  }

  private getActivities(
    activityGroupId: number,
    activitystreamIdentifier: number
  ): ActivityItem[] {
    const activities: ActivityItem[] = [];
    const activityType = this.getActivityType(activitystreamIdentifier);

    this.activityItems
      .filter((x) => x.isActive)
      .forEach((x) => {
        const assignedActivities =
          this.getCurrentActivityStreamActivitiesAssignedToCurrentGroup(
            x.id,
            activityGroupId,
            activityType
          );

        if (
          this.dprActivitiesDurationHistory.find(
            (z) =>
              z.activityId === x.id &&
              z.activityGroupId === activityGroupId &&
              z.parallelActivityType === activityType
          ) ||
          assignedActivities.length > 0
        ) {
          activities.push({
            activity: x.name,
            totalDurationToday: minutesToHoursAndMinutes(
              this.getTotalMinutes(assignedActivities)
            ),
            accumulatedDurationToDate: this.getActivityAccumulatedTotalDuration(
              activityGroupId,
              x.id,
              this.getActivityType(activitystreamIdentifier),
              this.getTotalMinutes(assignedActivities)
            ),
          });
        }
      });

    return activities;
  }

  private getCurrentActivityStreamGroups(
    activityGroupId: number,
    activityType: number
  ): DprActivity[] {
    const activeActivitiesIds = this.activityItems
      .filter((x) => x.isActive)
      .map((x) => x.id);
    return activityType === 0
      ? this.dprActivitiesList.filter(
          (y) =>
            !y.parallelActivityType &&
            y.activityGroupId === activityGroupId &&
            activeActivitiesIds.includes(y.activityId)
        )
      : this.dprActivitiesList.filter(
          (y) =>
            y.activityGroupId === activityGroupId &&
            y.parallelActivityType === activityType &&
            activeActivitiesIds.includes(y.activityId)
        );
  }

  private getCurrentActivityStreamActivitiesAssignedToCurrentGroup(
    activityId: number,
    activityGroupId: number,
    activityType: number
  ): DprActivity[] {
    return activityType === 0
      ? this.dprActivitiesList.filter(
          (y) =>
            !y.parallelActivityType &&
            y.activityGroupId === activityGroupId &&
            y.activityId === activityId
        )
      : this.dprActivitiesList.filter(
          (y) =>
            y.activityId === activityId &&
            y.activityGroupId === activityGroupId &&
            y.parallelActivityType === activityType
        );
  }

  private getActivityType(activitystreamIdentifier: number): number {
    return (
      this.activityStreams.find(
        (x) => x.parallelActivityType === activitystreamIdentifier
      ).parallelActivityType ?? 0
    );
  }

  private getActivityStreamName(activityStreamIdentifier: number): string {
    return this.activityStreams.find(
      (activityStream) =>
        activityStream.parallelActivityType === activityStreamIdentifier
    ).value;
  }

  private isActivityStreamActive(activityStreamIdentifier: number): boolean {
    return this.activityStreams.find(
      (activityStream) =>
        activityStream.parallelActivityType === activityStreamIdentifier
    ).isActive;
  }

  private getTotalMinutes(activities: DprActivity[]): number {
    let minutesSpent = 0;

    activities.forEach((y) => {
      minutesSpent += computeDiffTimeAsMinutes(
        convertTimeToDate(y.finish),
        convertTimeToDate(y.start)
      );
    });

    return minutesSpent;
  }
  private getActivityGroupAccumulatedDurationToDate(
    activityGroupId: number,
    activityType: number,
    totalDurationSpentToday: number
  ): string {
    let totalDuration = 0;
    const activeActivitiesIds = this.activityItems
      .filter((x) => x.isActive)
      .map((x) => x.id);

    this.dprActivitiesDurationHistory.forEach((x) => {
      if (
        x.activityGroupId === activityGroupId &&
        x.parallelActivityType === activityType &&
        activeActivitiesIds.includes(x.activityId)
      ) {
        totalDuration += x.totalDuration;
      }
    });
    return minutesToHoursAndMinutes(totalDuration + totalDurationSpentToday);
  }

  private getActivityAccumulatedTotalDuration(
    activityGroupId: number,
    activityId: number,
    activityType: number,
    totalDurationSpentToday: number
  ): string {
    let totalDuration = 0;

    this.dprActivitiesDurationHistory.forEach((x) => {
      if (
        x.activityGroupId === activityGroupId &&
        x.parallelActivityType === activityType &&
        x.activityId === activityId
      ) {
        totalDuration += x.totalDuration;
      }
    });

    return minutesToHoursAndMinutes(totalDuration + totalDurationSpentToday);
  }

  private isGroupIncludedInActivityStream(
    activityGroupId: number,
    activityType: number
  ): boolean {
    return activityType !== 0
      ? this.dprActivitiesList.some(
          (x) =>
            x.activityGroupId === activityGroupId &&
            x.parallelActivityType === activityType
        )
      : this.dprActivitiesList.some(
          (x) =>
            x.activityGroupId === activityGroupId && !x.parallelActivityType
        );
  }
}
